import { Board } from './enums';

/**
 * Where the read-only WAN IP address is read from:
 * - `host` — the device's own `eth0` interface (v2 / Play / Sunberry).
 * - `mikrotik` — an attached MikroTik router queried over SSH (v1).
 * - `false` — no WAN address is available/shown.
 */
export type WanAddressSource = 'host' | 'mikrotik' | false;

/**
 * Coarse per-board network feature groups.
 *
 * - `managedNetwork` — the full managed-network stack: DHCP leases/hosts,
 *   WiFi AP, host scanning and static-WAN configuration.
 * - `wanAddress` — source of the read-only WAN IP address (or `false`).
 * - `wanMac` — show the device WAN MAC address (`DEVICE_MAC`, the `eth0` MAC).
 * - `internalNetwork` — the board has the internal Infigy network behind its own
 *   router/bridge (`INTERNAL_NETWORK_RANGE`). Sunberry sits on the external
 *   network only. Note this is broader than `managedNetwork`: v1 has the
 *   internal network too, it is just managed by the MikroTik instead.
 *
 * The online-status (internet / cloud / VPN) indicators are intentionally NOT a
 * capability: the ping-based checks work regardless of board hardware, so they
 * are always shown.
 *
 * Capabilities are derived purely from the board type. To support a new board
 * with different feature support, add a branch to `getNetworkCapabilities`.
 */
export type NetworkCapabilities = {
  managedNetwork: boolean;
  wanAddress: WanAddressSource;
  wanMac: boolean;
  internalNetwork: boolean;
};

export function getNetworkCapabilities(board: Board): NetworkCapabilities {
  const managed = board === Board.V2 || board === Board.Play;
  const hostWan = managed || board === Board.SunBerry;

  return {
    managedNetwork: managed,
    wanAddress: hostWan ? 'host' : board === Board.V1 ? 'mikrotik' : false,
    wanMac: hostWan,
    internalNetwork: managed || board === Board.V1,
  };
}
